export const fr = {
  // Navigation
  nav: {
    products: 'Products',
    faq: 'FAQ',
    login: 'Se connecter',
    contact: 'Entrer en contact',
  },

  // Hero Section
  hero: {
    title: 'Découvrez Talentees,',
    titleHighlight: "l'allié du recrutement de votre entreprise.",
    subtitleBefore: "Accélérez tout votre pipeline de recrutement. ",
    subtitleHighlight: "Tout d'un coup,",
    subtitleAfter: " ou une étape à la fois.",
    cta: 'Entrer en contact',
    scrollHint: 'Défiler pour explorer',
  },

  // Narrative Chapter
  narrative: {
    slide1: "Talentees transforme le recrutement en une chaîne end-to-end — du sourcing à l'offre.",
    slide2: "Un bon recrutement est une suite de décisions cohérentes :\nmêmes critères, mêmes preuves, du début à la fin.",
    slide3: "Aujourd'hui, démarrez par le screening.\nDemain, activez chaque agent étape par étape.",
  },

  // Capabilities Section (Recruitment Value Chain)
  capabilities: {
    title: 'La chaîne de valeur du recrutement',
    subtitle: 'Talentees automatise intelligemment les étapes clés de votre processus de recrutement.',
    steps: {
      needDefinition: {
        step: 'Définition du besoin',
        description: 'Identification du poste et des critères',
      },
      sourcing: {
        step: 'Sourcing',
        description: 'Recherche et approche des candidats',
        benefit: 'Identification automatique des meilleurs profils sur tous les canaux',
      },
      screening: {
        step: 'Screening CV',
        description: 'Analyse et tri des candidatures',
        benefit: 'Analyse intelligente : score, signaux, risques et questions personnalisées',
      },
      hrInterview: {
        step: 'Entretien RH',
        description: 'Premier échange avec les candidats',
        benefit: "Entretien autonome ou assistance en temps réel pour structurer l'évaluation",
      },
      technicalTest: {
        step: 'Test technique',
        description: 'Évaluation des compétences pratiques',
        benefit: 'Génération et évaluation automatique de cas pratiques adaptés',
      },
      managerInterview: {
        step: 'Entretien manager',
        description: 'Validation par le responsable',
        benefit: "Assistant intelligent pour guider et structurer l'entretien",
      },
      proposal: {
        step: 'Proposition & Négociation',
        description: 'Offre finale et closing',
      },
    },
    legend: {
      automated: 'Automatisé par Talentees',
      automatedDesc: 'IA spécialisée pour chaque étape',
      manual: 'Processus manuel',
      manualDesc: 'Géré par votre équipe',
    },
    stats: {
      automatedSteps: 'Étapes automatisées',
      timeReduction: 'Réduction du temps de traitement',
      objectivity: 'Objectivité des évaluations',
    },
  },

  // Agents Section
  agents: {
    title: 'Nos agents IA',
    subtitle: "Chaque étape du recrutement orchestrée par un agent spécialisé.\nDu sourcing à l'offre, un pipeline end-to-end.",
    available: 'Disponible maintenant',
    comingSoon: 'Bientôt disponible',
    learnMore: 'En savoir plus',
    keyFeatures: 'Fonctionnalités clés',
    features: [
      'Automatisation intelligente',
      'Analyse contextuelle approfondie',
      'Rapports structurés et actionnables',
    ],
  },

  // Agent Names and Descriptions
  agentDetails: {
    victor: {
      name: 'Victor',
      step: 'Analyse CV (Screening)',
      description: 'Transforme un CV en décision : score multidimensionnel, signaux clés, risques identifiés et questions personnalisées.',
    },
    nora: {
      name: 'Nora',
      step: 'Sourcing',
      description: 'Identifie et approche les meilleurs profils sur les bons canaux avec une précision chirurgicale.',
    },
    clara: {
      name: 'Clara',
      step: 'Entretien autonome',
      description: "Mène l'entretien de bout en bout avec intelligence contextuelle et produit un rapport structuré et comparable.",
    },
    milo: {
      name: 'Milo',
      step: 'Assistant entretien',
      description: "Assiste le recruteur en temps réel : guide la conversation, note les points clés et structure l'évaluation.",
    },
    iris: {
      name: 'Iris',
      step: 'Test case',
      description: 'Génère et évalue des cas pratiques adaptés au poste avec restitution claire et critères objectifs.',
    },
  },

  // Pipeline Section
  pipeline: {
    label: 'Le workflow',
    title: "L'IA qui travaille avec vous,",
    titleMuted: 'pas à votre place.',
    subtitle: "Vous gardez le contrôle et les décisions. L'IA prend en charge l'analyse, la structuration et les tâches répétitives.",
    available: 'Disponible',
    comingSoon: 'Bientôt',
    steps: {
      jobs: {
        label: 'Feed with Jobs',
      },
      sourcing: {
        label: 'Sourcing',
        you: 'Vous définissez le poste et les critères.',
        ai: "L'IA identifie et approche les profils sur tous les canaux.",
      },
      screening: {
        label: 'Screening',
        you: 'Vous prenez la décision finale.',
        ai: "L'IA analyse chaque CV — score, signaux clés, risques, questions recommandées.",
      },
      interview: {
        label: 'Interview Copilot',
        you: "Vous menez l'entretien, vous évaluez.",
        ai: "L'IA vous guide en temps réel, structure vos notes et produit le rapport.",
      },
      testcase: {
        label: 'Test Case',
        you: 'Vous validez le cas et évaluez le candidat.',
        ai: "L'IA génère le cas pratique adapté au poste avec des critères objectifs.",
      },
    },
  },

  // FAQ Section
  faq: {
    title: 'FAQ.',
    questions: [
      {
        question: 'Comment ça marche ?',
        answer: "Importez un CV et une fiche de poste. En moins de 30 secondes, Talentees génère un rapport complet : score multidimensionnel, synthèse du profil, signaux clés, risques identifiés et questions d'entretien personnalisées. Vous gardez la main sur la décision finale.",
      },
      {
        question: 'Quelles données sont stockées ?',
        answer: "Seuls les CV et les rapports générés sont conservés. La rétention par défaut est de 90 jours, avec suppression sur demande à tout moment. Talentees est conforme RGPD et aucune donnée n'est utilisée pour entraîner des modèles tiers.",
      },
      {
        question: 'Comment garantir la transparence ?',
        answer: "Chaque score est justifié par des citations directes du CV. Vous voyez exactement pourquoi un profil est bien ou mal noté — pas de boîte noire. Un audit trail complet est disponible pour chaque évaluation.",
      },
      {
        question: 'Roadmap produit ?',
        answer: "Le Sourcing et le Screening sont disponibles dès maintenant. L'Interview Copilot et le Test Case sont en cours de déploiement — activables progressivement selon vos besoins, en pipeline complet ou en pay as you go.",
      },
    ],
  },

  // Final CTA Section
  finalCta: {
    title: 'Prêt à transformer votre recrutement ?',
    subtitle: "Rejoignez les entreprises qui recrutent plus vite et mieux grâce à l'IA.",
    cta: 'Entrer en contact',
  },

  // Footer
  footer: {
    description: "Le recrutement IA, de bout en bout. Accélérez vos embauches avec l'intelligence artificielle.",
    products: 'Products',
    company: 'Company',
    companyLinks: {
      about: 'À propos',
      careers: 'Carrières',
    },
    support: 'Support',
    supportLinks: {
      documentation: 'Documentation',
    },
    legal: 'Legal',
    legalLinks: {
      privacy: 'Confidentialité',
      terms: 'CGU',
      cookies: 'Cookies',
    },
    copyright: 'Talentees. Tous droits réservés.',
  },

  // Contact Modal
  contact: {
    step1: {
      title: 'Vos coordonnées',
      subtitle: 'Commençons par faire connaissance',
      firstName: 'Prénom',
      lastName: 'Nom',
      email: 'Email professionnel',
    },
    step2: {
      title: 'Quels sont vos besoins ?',
      subtitle: 'Sélectionnez un ou plusieurs services',
      options: {
        screening: 'Screening de CV',
        sourcing: 'Sourcing de candidats',
        interview: 'Entretiens IA',
        caseStudies: 'Case studies / Tests techniques',
      },
    },
    step3: {
      title: 'Votre entreprise',
      subtitle: 'Parlez-nous de votre entreprise',
      companyName: 'Nom de l\'entreprise',
      companyDescription: 'Décrivez votre activité et vos enjeux de recrutement',
    },
    navigation: {
      step: 'Étape',
      of: 'sur',
      back: 'Retour',
      continue: 'Continuer',
      send: 'Envoyer',
      sending: 'Envoi en cours...',
      pressEnter: 'Appuyez sur Entrée pour continuer',
    },
    success: 'Merci ! Nous vous contacterons très bientôt.',
    error: 'Une erreur est survenue. Veuillez réessayer.',
  },

  // Legal Pages
  legal: {
    hub: {
      title: 'Espace légal',
      subtitle: 'Retrouvez toutes nos informations légales et politiques de confidentialité.',
    },
    privacy: {
      title: 'Politique de confidentialité',
    },
    terms: {
      title: "Conditions Générales d'Utilisation",
    },
    cookies: {
      title: 'Politique des cookies',
    },
    dpa: {
      title: 'Data Processing Agreement',
    },
    candidatesPrivacy: {
      title: 'Confidentialité des candidats',
    },
    backToHub: "Retour à l'espace légal",
    backToHome: "Retour à l'accueil",
  },

  // About Page
  about: {
    label: 'À propos',
    title: 'On croit que le meilleur',
    titleMuted: 'recrutement est humain.',
    intro: "Talentees est né d'un constat simple : les recruteurs passent trop de temps sur des tâches à faible valeur ajoutée, et pas assez sur ce qui compte vraiment — comprendre un candidat, sentir un fit, prendre une bonne décision. Notre mission est de leur rendre ce temps, en leur donnant une IA qui travaille avec eux, pas à leur place.",
    valuesLabel: 'Nos valeurs',
    values: [
      {
        label: 'Le recruteur reste au centre',
        description: "L'IA n'est pas là pour remplacer le jugement humain — elle est là pour l'amplifier. Chaque décision reste entre les mains du recruteur.",
      },
      {
        label: 'Transparence totale',
        description: "Aucune boîte noire. Chaque score, chaque signal, chaque recommandation est justifié avec des éléments concrets issus du dossier candidat.",
      },
      {
        label: 'Efficacité sans compromis',
        description: "Réduire le temps passé sur les tâches répétitives ne veut pas dire baisser les standards. On accélère le processus sans sacrifier la qualité des recrutements.",
      },
      {
        label: 'Modularité',
        description: "Chaque équipe est différente. Talentees s'adapte à votre workflow — pipeline complet ou étape par étape, selon vos besoins.",
      },
    ],
    ctaLabel: 'Une question ? Une idée ?',
    backHome: "← Retour à l'accueil",
  },

  // Careers Page
  careers: {
    label: 'Carrières',
    title: "On construit quelque chose",
    titleMuted: "d'ambitieux.",
    description: "Aucun poste ouvert pour l'instant — mais si vous pensez pouvoir contribuer, on est toujours preneurs d'une belle candidature.",
    cta: 'Candidature spontanée',
    backHome: "← Retour à l'accueil",
  },

  // Agents Showcase Page
  agentsShowcase: {
    title: 'Nos agents IA',
    subtitle: "Chaque étape du recrutement orchestrée par un agent spécialisé.\nDu sourcing à l'offre, un pipeline end-to-end.",
    navHint: 'Naviguer entre les agents',
  },
};

export type Translations = typeof fr;
